import { useEffect } from 'react';
import type { Row } from 'react-table';

import type { EntityModel } from '../../../pages/Trafficking/TraffickingPage/modelConverters';

const DEFAULT_ROW_HEIGHT = 45;
const SCROLL_CONTAINER_ID = 'scroll-container';

interface UseScrollToSelectedRowOptions {
  rows: Row<EntityModel>[];
  navigationTargetId?: string | null;
  onNavigationScrollComplete?: () => void;
}

function getRowHeight(): number {
  const container = document.getElementById(SCROLL_CONTAINER_ID);
  if (!container) return DEFAULT_ROW_HEIGHT;
  const firstRow = container.querySelector<HTMLElement>("tbody tr[role='row']");
  if (!firstRow) return DEFAULT_ROW_HEIGHT;
  const height = firstRow.getBoundingClientRect().height;
  return height > 0 ? height : DEFAULT_ROW_HEIGHT;
}

function scrollToRowIndex(rowIndex: number): void {
  const container = document.getElementById(SCROLL_CONTAINER_ID);
  if (!container) return;

  const rowHeight = getRowHeight();
  const targetScrollTop = Math.max(0, rowIndex * rowHeight);
  container.scrollTo({ top: targetScrollTop, behavior: 'instant' as ScrollBehavior });
}

function useScrollToSelectedRow({
  rows,
  navigationTargetId,
  onNavigationScrollComplete,
}: UseScrollToSelectedRowOptions): void {
  useEffect(() => {
    if (!navigationTargetId) return;

    const rowIndex = rows.findIndex((row) => row.id === navigationTargetId);
    if (rowIndex === -1) return;

    scrollToRowIndex(rowIndex);
    onNavigationScrollComplete?.();
  }, [navigationTargetId, rows, onNavigationScrollComplete]);
}

export default useScrollToSelectedRow;
